import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllCategories } from '../services/product-api';

/**
 * Maps a subcategory id to its { category, sub_category } slugs using the
 * shared, cached categories list. Some endpoints (e.g. search) only send the
 * subcategory id, and product URLs need both slugs.
 */
export const useCategoryLookup = ({ enabled = true } = {}) => {
  const { data, isSuccess } = useQuery({
    queryKey: ['categories'],
    queryFn: getAllCategories,
    enabled,
  });

  const bySubCategoryId = useMemo(() => {
    const map = new Map();
    for (const cat of data?.data?.categories ?? []) {
      for (const sub of cat.subcategories ?? []) {
        map.set(String(sub.id), { category: cat.slug, sub_category: sub.slug });
      }
    }
    return map;
  }, [data]);

  return { bySubCategoryId, isReady: isSuccess };
};

// Product page URL, or null when the product's category can't be resolved.
export const getProductPath = (product, bySubCategoryId) => {
  const productCategory = product?.produtCategory || product?.productCategory;
  const slugs =
    productCategory?.parent_category?.slug && productCategory?.slug
      ? {
          category: productCategory.parent_category.slug,
          sub_category: productCategory.slug,
        }
      : bySubCategoryId.get(String(productCategory));
  if (!slugs || !product?.productID) return null;
  const slug = (product.slug || '').replace(/\//g, '');
  return `/${slugs.category}/${slugs.sub_category}/${product.productID}/${slug}`;
};
